import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { baseURL } from '../../../../api/url';
import backgroundImage from '../../../../assets/background.jpg';
import capRule1 from '../../../../assets/cap_rules/cap_rule_1.png';
import capRule2 from '../../../../assets/cap_rules/cap_rule_2.png';
import capRule2_2 from '../../../../assets/cap_rules/cap_rule_2_2.svg';
import capRule3 from '../../../../assets/cap_rules/cap_rule_3.png';
import logo from '../../../../assets/logos/aye-logo.png';
import PayNetFooter from '../../../../shared/PayNetFooter';

interface CaptureRulesProps {
  isOngLayeFlow?: boolean;
  isCheckoutFlow?: boolean;
  /**
   * When false the user came from a checkout session that does not require
   * payment. The component adjusts wording accordingly.
   */
  paymentRequired?: boolean;
}

export default function CaptureRules({
  isOngLayeFlow = false,
  isCheckoutFlow = false,
  paymentRequired = true
}: CaptureRulesProps) {
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const clearRegistrationSessionState = () => {
    const externalSessionExpiresAt = sessionStorage.getItem(
      'external_session_expires_at'
    );
    const hasValidExternalSession =
      !!externalSessionExpiresAt &&
      new Date(externalSessionExpiresAt).getTime() > Date.now();

    const registrationKeysToClear = [
      'registration_started',
      'step',
      'face_index',
      'phoneComplete',
      'ong_key'
    ];

    registrationKeysToClear.forEach((key) => sessionStorage.removeItem(key));

    if (!hasValidExternalSession) {
      const externalKeysToClear = [
        'external_session_id',
        'external_cust_id',
        'external_prefill_phone_number',
        'external_prefill_country_code',
        'external_prefill_full_name',
        'external_prefill_email',
        'external_session_expires_at'
      ];

      externalKeysToClear.forEach((key) => sessionStorage.removeItem(key));
    }
  };

  const ready = async () => {
    try {
      setIsLoading(true);

      // For OngLaye flow, skip face scanning and go directly to account details
      if (isOngLayeFlow) {
        // Get the stored biz_token from OngLaye validation
        const bizToken = sessionStorage.getItem('onglaye_biz_token');

        if (!bizToken) {
          console.error('OngLaye biz_token not found in session');
          setIsLoading(false);
          return;
        }

        // Set up session for registration flow
        sessionStorage.setItem('registration_started', 'true');
        sessionStorage.setItem('step', '2');
        sessionStorage.setItem('face_index', bizToken);

        navigate('/register/payment', { replace: true });
        return;
      }

      // For Checkout flow, skip face scanning and go directly to account details
      if (isCheckoutFlow) {
        // Get the stored biz_token from checkout session validation
        const bizToken = sessionStorage.getItem('checkout_biz_token');

        if (!bizToken) {
          console.error('Checkout biz_token not found in session');
          setIsLoading(false);
          return;
        }

        // Set up session for registration flow
        sessionStorage.setItem('registration_started', 'true');
        sessionStorage.setItem('step', '2');
        sessionStorage.setItem('face_index', bizToken);

        navigate('/register/payment', { replace: true });
        return;
      }

      // Regular flow - get biz token from Tencent for face scanning
      clearRegistrationSessionState();
      localStorage.removeItem('LoggedIn');
      localStorage.removeItem('token');

      const url = `${baseURL}/face/register`;
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ screen: 'register' })
      });
      const data = (await response.json()) as {
        success: boolean;
        status: number;
        data?: { VerificationUrl: string; BizToken: string };
        error?: { code: string };
      };

      setIsLoading(false);
      if (data.success) {
        sessionStorage.setItem('registration_started', 'true');
        sessionStorage.setItem('step', '1');
        sessionStorage.setItem('face_index', data?.data?.BizToken || '');
        window.location.href = data?.data?.VerificationUrl || '';
      }
    } catch (error) {
      setIsLoading(false);
      console.error('Error fetching verification URL:', error);
    }
  };

  // For OngLaye or Checkout flow, show modified step 1 text since face scan is already done
  const isExternalFlow = isOngLayeFlow || isCheckoutFlow;

  const step1Text = isExternalFlow
    ? 'Face scan completed! Your face has already been verified.'
    : 'Your scan is private. No image is stored or shared.';

  const step1Label = isExternalFlow ? 'Face Verified ✓' : 'Scan Face';

  return (
    <div className="flex h-screen w-full flex-col items-center justify-center bg-gray-50 pt-12 sm:mx-0">
      <div className="flex flex-col items-center justify-center">
        <img src={logo} alt="Aye Logo" className="h-8 w-16" />
      </div>
      <div className="mt-8 flex flex-col items-center justify-center text-gray-600">
        <p className="mx-4 max-w-full text-center text-lg font-semibold sm:mx-0">
          How AyeFace Works
        </p>
        {isExternalFlow && (
          <p className="mx-4 mt-2 max-w-full text-center text-sm text-emerald-600 sm:mx-0">
            {isCheckoutFlow
              ? "🛒 You're registering from checkout!"
              : "🎉 You're registering from your Lucky Face win!"}
          </p>
        )}
      </div>

      <div className="mb-8 mt-2 flex w-full max-w-4xl flex-col items-center justify-center px-8">
        {/* Step 1 */}
        <div className="relative mb-6 w-full">
          <div
            className={`absolute -left-4 -top-1 z-10 flex h-6 w-6 items-center justify-center rounded-full text-sm font-bold text-white shadow-md ${
              isExternalFlow
                ? 'bg-emerald-500 shadow-emerald-500/50'
                : 'bg-[#FF8563] shadow-orange-500/50'
            }`}
          >
            {isExternalFlow ? '✓' : '1'}
          </div>
          <div
            className={`rounded-2xl px-4 py-3 text-xs shadow-lg sm:text-sm ${
              isExternalFlow ? 'bg-emerald-50' : 'bg-white'
            }`}
          >
            <div
              className="flex items-start justify-between gap-3 rounded-2xl pt-2"
              style={{
                backgroundImage: `url(${backgroundImage})`,
                backgroundPosition: '60% 50%'
              }}
            >
              <div className="flex h-28 flex-1 items-center px-2">
                <p>{step1Text}</p>
              </div>
              <div className="flex h-28 w-24 flex-shrink-0 items-center justify-center overflow-hidden pr-1 sm:pr-8">
                <img
                  src={capRule1}
                  alt="Scan Face"
                  className="h-full w-full translate-y-5 scale-150 object-cover"
                />
              </div>
            </div>
            <p
              className={`mt-2 text-center font-semibold ${
                isExternalFlow ? 'text-emerald-600' : 'text-gray-400'
              }`}
            >
              {step1Label}
            </p>
          </div>
        </div>

        {/* Step 2 */}
        <div className="relative mb-6 w-full">
          <div className="absolute -left-4 -top-1 z-10 flex h-6 w-6 items-center justify-center rounded-full bg-[#FF8563] text-sm font-bold text-white shadow-md shadow-orange-500/50">
            {isExternalFlow ? '1' : '2'}
          </div>
          <div className="rounded-2xl bg-white px-4 py-3 text-xs shadow-lg sm:text-sm">
            <div
              className="flex items-start justify-between gap-3 rounded-2xl pt-2"
              style={{
                backgroundImage: `url(${backgroundImage})`,
                backgroundPosition: '60% 50%'
              }}
            >
              <div className="flex h-28 flex-1 items-center px-2">
                <p>
                  {paymentRequired ? (
                    <>
                      We do not store card info. Payments are handled by
                      PCI-DSS-certified{' '}
                      <a
                        href="https://www.fiuu.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 underline"
                      >
                        Fiuu
                      </a>
                      .
                    </>
                  ) : (
                    <>Complete your basic details to secure your account.</>
                  )}
                </p>
              </div>
              <div className="flex h-28 w-24 flex-shrink-0 items-center justify-center overflow-hidden pr-1 sm:pr-8">
                <img
                  src={capRule2_2}
                  alt="bg"
                  className="absolute right-4 border-0 object-cover shadow-none drop-shadow-none"
                />
                <img
                  src={capRule2}
                  alt="Setup Payment"
                  className="relative h-full w-full translate-y-5 scale-150 object-cover"
                />
              </div>
            </div>
            <p className="mt-2 text-center font-semibold text-gray-400">
              {paymentRequired ? (
                <>
                  Setup Personal Details and
                  <br />
                  Select a Payment Method to Link with your Face
                </>
              ) : (
                <>Setup Personal Details</>
              )}
            </p>
          </div>
        </div>

        {/* Step 3 */}
        <div className="relative mb-6 w-full">
          <div className="absolute -left-4 -top-1 z-10 flex h-6 w-6 items-center justify-center rounded-full bg-[#FF8563] text-sm font-bold text-white shadow-md shadow-orange-500/50">
            {isExternalFlow ? '2' : '3'}
          </div>
          <div className="rounded-2xl bg-white px-4 py-3 text-xs shadow-lg sm:text-sm">
            <div
              className="flex items-start justify-between gap-3 rounded-2xl pt-2"
              style={{
                backgroundImage: `url(${backgroundImage})`,
                backgroundPosition: '60% 50%'
              }}
            >
              <div className="flex h-36 flex-1 items-center px-2">
                <p>
                  {paymentRequired ? (
                    <>
                      Done! You&apos;re ready for secure face payments. Payment
                      methods and PIN limits can be added/removed anytime in the{' '}
                      <a
                        href={window.location.origin}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 underline"
                      >
                        User Portal
                      </a>
                      .
                    </>
                  ) : (
                    <>
                      Done! You&rsquo;re ready to use secure face verification.
                      Payment methods and PIN limits can be added or managed
                      anytime in the{' '}
                      <a
                        href={window.location.origin}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 underline"
                      >
                        User Portal
                      </a>
                      .
                    </>
                  )}
                </p>
              </div>
              <div className="flex h-36 w-28 flex-shrink-0 items-center justify-center overflow-hidden pr-1 sm:pr-8">
                <img
                  src={capRule3}
                  alt="Registration Successful"
                  className="h-full w-full translate-y-3 scale-150 object-cover"
                />
              </div>
            </div>
            <p className="mt-2 text-center font-semibold text-gray-400">
              Registration Successful
            </p>
          </div>
        </div>
      </div>

      {/* I'm ready button */}
      <button
        type="button"
        disabled={isLoading}
        aria-label="I'm ready"
        onClick={ready}
        className="mb-4 w-48 rounded-2xl bg-[#5CB891] px-2 py-4 font-semibold text-white transition-all duration-300 ease-out hover:bg-[#4aa07c]"
      >
        {isLoading ? 'Loading...' : isExternalFlow ? 'Continue' : "I'm ready"}
      </button>

      <PayNetFooter />
    </div>
  );
}
